<template lang="pug">
.live-player
  .player-main.main-container-fluid(:class="{'has-message': message, 'chat-open': viewMobileChat}")
    .player-window
      .rogue-hide(v-if="renderTranslations")
        .text(v-html="stream.description" v-translate v-for="stream in streams")
      MMV(
        ref="mmvPlayer"
        :activeStream="activeStream"
        :hasTicket="hasTicket"
        :supportSplitScreen="supportSplitScreen"
        :showCustomControls="showCustomControls"
        :splitNames="splitNames"
        @setSplit="setSplitNames($event)"
        :ybvrSupported="ybvrSupported"
        :baseUrl="competition.baseUrl"
        :autoplay="competition.state === 'live'"
      )
    //- Render only this breakpoint's list; hiding the other with CSS could leave both visible
    client-only
      StreamLinkList.desktop-stream-list(
        v-if="!isMobile"
        :activeStream="activeStream"
        :splitNames="splitNames"
        @setSplit="setSplitNames($event)"
        :hasTicket="hasTicket"
        :ybvrSupported="ybvrSupported"
        :showMultiview="showMultiviewButton"
        :multiviewDrawerOpen="!!mmvPlayer?.showSplit"
        @toggleMultiview="mmvPlayer?.toggleSplitDrawer()"
        :showFullscreen="showFullscreenButton"
        @toggleFullscreen="mmvPlayer?.toggleFullscreen()"
      )
    client-only(v-if="activePlayerTab === 'Chat' || (isMobile && viewMobileChat)")
      .chat
        live-chat(:chat-key="competition.compId" @close="closeChat()" :has-ticket="hasTicket" :hasActiveTrial='!!activePremiumTrial')
    .player-desc
      event-video-description(:event="activeEvent" :competition="competition" :stream="activeStream" :splitNames="splitNames")
      client-only
        StreamLinkList.mobile-stream-list(
          v-if="isMobile"
          mode="light"
          :activeStream="activeStream"
          :splitNames="splitNames"
          @setSplit="setSplitNames($event)"
          :hasTicket="hasTicket"
          :ybvrSupported="ybvrSupported"
          :showMultiview="showMobileMultiviewButton"
          :multiviewDrawerOpen="!!mmvPlayer?.showSplit"
          @toggleMultiview="mmvPlayer?.toggleSplitDrawer()"
          :showFullscreen="showMobileFullscreenButton"
          @toggleFullscreen="mmvPlayer?.toggleFullscreen()"
        )
      .main-container-wide.leaderboard-wrapper
        StrongestLeaderboard(:initialId="competition.compId" :pickList="competition.pickList")
    .player-message(v-if="message && loaded" @click="messageClick($event)")
      Message(v-bind="message")
    .mobile-content(v-if="eventHasPremium && competition.state === 'live'")
      .chat-btn(@click="viewChat()")
        | {{ __('Live Chat', eventNamespace) }}
        Icon.right-arrow(name="arrow-right")
    Tabs.player-tabs(
      :tabs="playerTabNames"
      :tab-status="playerTabStatus"
      v-model="activePlayerTab"
      theme="player"
      align="left"
    )
    client-only
      Tabs.mobile-tabs(
        :tabs="mobileTabNames"
        :tab-status="playerTabStatus"
        v-model="activeTab"
        theme="player"
        align="left"
      )
    .streams(v-if="(isMobile && activeTab === 'Live Stream') || (!isMobile && activePlayerTab === 'Live Stream')")
      slot.live(name="live")
    .replays-tab(v-if="(isMobile && activeTab === 'Replays') || (!isMobile && activePlayerTab === 'Replays')")
      slot.replays-slot(name="replaysTab")
    .tailgate-tab(v-if="(isMobile && activeTab === 'Tailgate Show') || (!isMobile && activePlayerTab === 'Tailgate Show')")
      slot.tailgate-slot(name="tailgateTab")  
    .main-container-wide.equip(v-show="activeTab === equipmentTitle || !isMobile")
      slot(name="equipment")
    .main-container-wide.leaderboard(v-show="activeTab === 'Leaderboard'")
      StrongestLeaderboard(:initialId="competition.compId" :pickList="competition.pickList")
    .main-container-wide.highlights(:class="activeTab === shortsTitle && 'show-highlights'" data-allow-mismatch="class")
      Shorts(v-if="(activeStream || {}).streamType === 'Replays' && activeEvent?.shortsPlaylistId" :playlist-id="activeEvent.shortsPlaylistId" :title="shortsTitle")
      Shorts(v-else-if="competition?.shortsPlaylistId" :playlist-id="competition.shortsPlaylistId" :title="shortsTitle")
    .support(v-if="eventHasPremium")
      .link(@click="showSupport = true") {{__('Live Player FAQ', eventNamespace)}}
  Modal(v-if="showSupport" title="Iron Game Premium Media Viewer FAQ" @close="showSupport = false")
    .support-body
      Accordion(heading="Can I watch replays of past events?")
        p {{__('After an event finishes, premium replays are processed and uploaded to the Event Replay page.', eventNamespace)}}
        p {{__('Check the Schedule page to view past event information, including replays, leaderboards, and equipment.', eventNamespace)}}
        a(href="/invitational/schedule") {{__('View the Schedule', eventNamespace)}}
      Accordion(heading="Can I watch Iron Game Premium on my TV?" )
        p {{__('Iron Game Premium supports "casting" to both Google Chromecast devices and Apple devices that use AirPlay.', eventNamespace)}}
        p {{__('To start, make sure that your device with Iron Game Premium is on the same WiFi network as your Chromecast or AirPlay device.', eventNamespace)}}
        h4 {{__('For AirPlay:', eventNamespace)}}
        p {{__('After starting your Iron Game Premium stream, click the "AirPlay" icon in the Media Viewer toolbar', eventNamespace)}}
        h4 {{__('For Chromecast:', eventNamespace)}}
        p {{__('After starting your Iron Game Premium stream, click the "Music, Videos, and More" icon in the top right of the browser.', eventNamespace)}}
      Accordion(heading="What browsers and devices are supported?" )
        p
          | {{__('Live Streaming is supported in all major browsers, but some features like split screen multiview require a modern browser', eventNamespace)}}
          | {{__('like Google Chrome or Safari.', eventNamespace)}}
      Accordion(heading="How do I turn on Closed Captioning for live streams?" )
        p
          | {{__('Closed Captions are available in Google Chrome with the Live Caption setting.', eventNamespace)}}
          | {{__('For assistance turning on Live Caption, please view the follow link:', eventNamespace)}}
        a(href="https://support.google.com/chrome/answer/10538231?hl=en" target="_blank") {{__('Use Live Caption in Chrome', eventNamespace)}}
      Accordion(heading="I see a placeholder image instead of a stream. Is the player broken?")
        p {{__('Iron Game Premium player streams are only active during live events.', eventNamespace)}}
      Accordion(heading="I am an international customer and am having trouble checking out")
        p {{__('Please try using PayPal as a checkout method.', eventNamespace)}}
        p {{__('If you experience issues, contact our support team during the checkout process.', eventNamespace)}}
      Accordion(heading="Still need help?")
        p {{__('If you are still experiencing playback issues, please fill in the form below to submit a support request to our team.', eventNamespace)}}
        p {{__('Note: By submitting this form, you agree to automated browser information collection that will help our team troubleshoot your issue.', eventNamespace)}}
        RogueSelect(label="Device type" :options="deviceOpts" v-model="support.device" )
        RogueTextarea(label="Please explain the issue you are experiencing" v-model="support.text")
        RogueButton(@click="sendSupport()") {{__('Send Message', eventNamespace)}}
        Message(v-if="supportSent") {{__('Your request has been received.'), eventNamespace}}
</template>
<script setup lang="ts">
import { ref, computed, inject, useSSRContext, onMounted, onBeforeUnmount, watch } from 'vue'
import { eventNamespace } from '../../js/constants'
import { useRogue } from '../../js/rogue'
import { useMQ } from '../../js/mq'
import MMV from './MMV.vue'
import LiveChat from './LiveChat.vue'
import EventVideoDescription from './EventVideoDescription.vue'
import StreamLinkList from './StreamLinkList.vue'

const props = withDefaults(defineProps<{
  competition: Record<string, any>
  events?: boolean
  activeStream: Record<string, any>
  activeEvent?: Record<string, any>
  liveStream?: boolean
  tailgateStream?: boolean
  replayStream?: boolean
  eventHasPremium?: boolean
  hasTicket?: boolean
  trialAvailable?: boolean
  shortsTitle?: string
  equipmentTitle?: string
  supportSplitScreen?: boolean
  showCustomControls?: boolean
  requirePremium?: boolean
  activePremiumTrial?: Record<string, any>
  hasEquipment?: boolean
}>(), {
  events             : false,
  activeStream       : () => ({}),
  liveStream         : false,
  tailgateStream     : false,
  replayStream       : false,
  eventHasPremium    : false,
  hasTicket          : false,
  trialAvailable     : false,
  shortsTitle        : 'Highlights',
  equipmentTitle     : 'Event Equipment Used',
  supportSplitScreen : false,
  showCustomControls : true,
  requirePremium     : true,
  hasEquipment       : false,
})

const emit = defineEmits(['toggleMobileChat'])

const showOverlay = inject<(name: string) => void>('showOverlay', () => {})
const hideMbnBanner = inject<() => void>('hideMbnBanner', () => {})
const rogue = useRogue()
const ssrContext = rogue?.ssr ? useSSRContext() : undefined

// 3. Reactive State
const loaded = ref(false)
const activePlayerTab = ref('Live Stream')
const activeTab = ref('Shorts')
const storedMobileTab = ref('')
const storedPlayerTab = ref('')
const time = ref(Date.now())
const viewMobileChat = ref(false)
const showSupport = ref(false)
const supportSent = ref(false)
const renderTranslations = ref(false)
const splitNames = ref<string[]>([])
const mmvPlayer = ref<InstanceType<typeof MMV> | null>(null)

const support = ref({
  text   : '',
  device : 'iphone',
  log    : [] as string[],
})

const deviceOpts = [
  { label: 'Apple iPhone', value: 'iphone' },
  { label: 'Apple iPad', value: 'ipad' },
  { label: 'Android Phone', value: 'android' },
  { label: 'Windows PC', value: 'windows' },
  { label: 'Mac', value: 'mac' },
  { label: 'Other', value: 'other' },
]

const mq = useMQ()

const isMobile = computed(() => mq.value === 'mobile' || mq.value === 'tablet')

const playerTabNames = computed(() => {
  let tabNames = props.competition.tabList.split(',').map((item:string) => item.trim())
  if (props.requirePremium && props.competition.state === 'live'){
    tabNames.unshift('Chat')
  }
  
  return tabNames
})

const playerTabStatus = computed(() => {
  return playerTabNames.value.map(name => {
    switch (name) {
      case 'Live Stream':
        return { name, disabled: !props.liveStream }
      case 'Replays':
        return { name, disabled: !props.replayStream }
      case 'Tailgate Show':
        return { name, disabled: !props.tailgateStream }
      case 'Chat':
        return { name, disabled: !props.requirePremium }
      default:
        return { name, disabled: false }
    }
  })
})

const mobileTabNames = computed(() => {
  const tabs = playerTabNames.value.filter(tab => tab !== 'Chat')
  tabs.push('Leaderboard')

  if (props.hasEquipment) {
    tabs.push(props.equipmentTitle)
  }

  if (props.activeEvent?.shortsPlaylistId || props.competition?.shortsPlaylistId) {
    tabs.push(props.shortsTitle)
  }

  return tabs
})

const eventTabNames = computed(() => {
  const exclude = ['Live Stream', 'Replays', 'Tailgate Show', 'Leaderboard', props.equipmentTitle]
  
  return mobileTabNames.value.filter(tabName => !exclude.includes(tabName))
})

// With native controls there's no custom bar, so the multiview toggle moves to the stream list
const showMultiviewButton = computed(() => props.supportSplitScreen && !!mmvPlayer.value?.useNativeControls)
const showFullscreenButton = computed(() => !!mmvPlayer.value?.useNativeControls && !!mmvPlayer.value?.canFullscreen)
// Mobile never gets the shared controls, so these don't depend on useNativeControls
const showMobileMultiviewButton = computed(() => props.supportSplitScreen)
const showMobileFullscreenButton = computed(() => !!mmvPlayer.value?.canFullscreen)

const trialExpiration = computed(() => props.activePremiumTrial?.expiration)

const trialTimeRemaining = computed(() => {
  if (!trialExpiration.value || time.value > Date.parse(trialExpiration.value)) return 0
  
  return Date.parse(trialExpiration.value) - time.value
})

const formattedTimeRemaining = computed(() => {
  const mins = Math.floor((trialTimeRemaining.value / 1000) / 60)
  const secs = Math.floor(trialTimeRemaining.value / 1000) % 60
  
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
})

const message = computed(() => {
  if (!props.activeStream.hasAccess || trialTimeRemaining.value > 0) {
    return {
      message: (trialTimeRemaining.value > 0)
        ? `You have ${formattedTimeRemaining.value} remaining until your free trial period ends.`
        : 'Iron Game Premium is required to view this stream.',
      icon     : 'eye',
      linkText : props.trialAvailable ? 'Try it now' : 'Purchase Ticket',
      linkUrl  : '#',
      status   : 'warning',
    }
  }

  const startDate = props.activeStream.startDate
  const start = new Date(startDate || 0)
  const countdown = Math.ceil((start.getTime() - time.value) / 1000 / 60)

  if (countdown > 0) {
    const minutes = countdown % 60
    const hours = Math.floor(countdown / 60) % 24
    const days = Math.floor(countdown / 60 / 24)

    const timeParts:string[] = []
    if (days > 0) timeParts.push(`${days} day${days > 1 ? 's' : ''}`)
    if (days > 0 || hours > 0) timeParts.push(`${hours} hour${hours !== 1 ? 's' : ''}`)
    timeParts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`)

    return {
      message : `Stream will be available in ${timeParts.join(', ')}`,
      icon    : 'media/signal',
      status  : 'warning',
    }
  }

  return null
})

// YBVR isn't sopported on mobile browsers. For those, the app will be needed
const ybvrSupported = computed(() => {
  if (rogue?.appContext) return true
  if (typeof window === 'undefined' || !window?.navigator?.userAgent) return false

  const userAgent = window.navigator.userAgent.toLowerCase()
  const isMobileOS = ['android', 'iphone', 'ipad'].some(os => userAgent.includes(os))
  
  return !isMobileOS
})

activePlayerTab.value = props.activeStream?.streamType || playerTabNames.value[0]

if (isMobile.value) {
  activeTab.value = props.activeStream?.streamType || playerTabNames.value[0]
  storedMobileTab.value = eventTabNames.value[0]
} else {
  activeTab.value = eventTabNames.value[0]
}

watch(() => mq, (newVal, prevVal) => {
  const isMobileNow = newVal === 'mobile' || newVal === 'tablet'
  const wasMobile = prevVal === 'mobile' || prevVal === 'tablet'

  if (isMobileNow && !wasMobile) {
    // Switching to Mobile
    storedMobileTab.value = activeTab.value
    storedPlayerTab.value = activePlayerTab.value
    
    if (mobileTabNames.value.includes(activePlayerTab.value)) {
      activeTab.value = activePlayerTab.value
    }
    closeChat()
  } else if (!isMobileNow && wasMobile) {
    // Switching to Desktop
    if (playerTabNames.value.includes(activeTab.value)) {
      activePlayerTab.value = activeTab.value
      activeTab.value = storedMobileTab.value
    } else {
      activePlayerTab.value = storedPlayerTab.value
    }
    
    if (!eventTabNames.value.includes(activeTab.value)) {
      activeTab.value = storedMobileTab.value
    }
    closeChat()
  }
})

watch(() => props.activeStream?.streamType, (newVal) => {
  if (newVal) {
    activePlayerTab.value = newVal
    activeTab.value = newVal
  }
})

let timerInterval: ReturnType<typeof setInterval>

onMounted(() => {
  if (ssrContext?.queryParams?.SAVE_TRANSLATIONS) {
    renderTranslations.value = true
  }

  timerInterval = setInterval(() => {
    time.value = Date.now()
  }, 1000)
  
  loaded.value = true
})

onBeforeUnmount(() => {
  clearInterval(timerInterval)
})

const closeChat = () => {
  viewMobileChat.value = false
  emit('toggleMobileChat', false)
}

const setSplitNames = (value) => {
  splitNames.value = value
}

</script>
<style lang="scss" scoped>
.leaderboard-wrapper{
  margin-top: $space-s;
  margin-bottom: $space-s;

  @include not-desktop(){
    display: none;
  }
}

.highlights {
  display: block;
  
  @include not-desktop(){
    display: none;
    &.show-highlights {
      display: block;
    }
  }
}

.mobile-content {
  margin: $space-s 0;
}

.chat-btn {
  margin: $space-s $space-xs;
  background: $color-gray-x-light;
  border: 1px solid rgb(233, 233, 233);
  border-radius: 2px 0 0 2px;
  padding: $space-xs;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: $weight-bold;

  .right-arrow {
    height: 10px;
    width: 10px;
  }

  &:active {
    background-color: $color-gray-light;
  }
}

.streams,
.replays-tab, .tailgate-tab {
  margin: $space-s;
}

.player-window {
  background-color: white;
}

.player-message {
  margin: $space-s $space-xs;
  box-shadow: 0 2px 6px 0 rgb(210, 210, 210);
}

.purchase {
  margin: $space-xs 0;
}

.player-tabs {
  margin-top: $space-s;
  border: 1px solid $color-gray-light;

  :deep(button:disabled) {
    color: $color-gray-dark;
    border-bottom: $border-width-thick solid transparent;
    cursor: auto;
  }
}

@include not-desktop() {
  .player-tabs {
    display: none;
  }

  .event-tabs {
    display: none;
  }

  .mobile-tabs {
    display: block;
    margin-bottom: $size-s;

    :deep(button:disabled) {
      color: $color-gray-dark;
      border-bottom: $border-width-thick solid transparent;
      cursor: auto;
    }
  }
}

@include all-desktop() {
  .mobile-content {
    display: none;
  }

  .player-tabs {
    display: block;
  }

  .event-tabs {
    display: block;
  }

  .mobile-tabs {
    display: none;
  }

  .streams,
  .replays-tab, .tailgate-tab {
    margin: 0;
    margin-bottom: $space-s;
    border: 1px solid $color-gray-light;
    border-top: none;
    overflow-y: auto;
  }

  .player-main {
    display: grid;
    grid-template-areas: 'video tabs'
      'video tabcontent'
      'streamlist tabcontent'
      'desc desc'
      'eventtabs eventtabs'
      'highlights highlights'
      'leaderboard leaderboard'
      'equip equip'
      'support support';
    grid-row-gap: 0;
    grid-column-gap: $space-s;
    grid-template-columns: 1fr 375px;
    grid-template-rows: 65px auto 1fr;

    &.has-message {
      grid-template-areas: 'video message'
        'video tabs'
        'video tabcontent'
        'streamlist tabcontent'
        'desc desc'
        'eventtabs eventtabs'
        'highlights highlights'
        'leaderboard leaderboard'
        'equip equip'
        'support support';
      grid-template-rows: min-content 65px auto 1fr;
    }
  }

  .support {
    margin: $space-s 0;
    grid-area: support;
  }

  .highlights {
    margin-top: $space-s;
    margin-bottom: $space-s;
    grid-area: highlights;
  }

  .player-message {
    margin: $space-s $space-s 0 0;
    grid-area: message;
  }

  .player-window {
    grid-area: video;
    min-width: 0;
  }

  .desktop-stream-list {
    grid-area: streamlist;
  }

  .player-desc {
    grid-area: desc;
    min-width: 0;
    margin-top: $space-s;
  }

  .player-tabs {
    grid-area: tabs;
  }

  .event-tabs,
  .mobile-tabs {
    grid-area: eventtabs;
  }

  .streams,
  .replays-tab,
  .chat, 
  .tailgate-tab {
    grid-area: tabcontent;
    height: 0;
    min-height: 100%;
  }

  .chat,
  .player-tabs,
  .streams,
  .replays-tab, 
  .tailgate-tab,
  .purchase {
    margin-right: $space-s
  }

  .equip {
    grid-area: equip;
  }

  .leaderboard {
    grid-area: leaderboard;
  }

  .highlights {
    margin-top: $space-s;
    grid-area: highlights;
    display: block;
  }
}

.support {
  text-align: center;
  padding: $space-s;
  color: $color-gray-light;

  .link {
    font-size: $size-xs;
    color: $color-gray-dark;
  }
}

.support-body {
  height: 400px;
  overflow-y: auto;
}

.highlights {
  margin-bottom: $space-m;
}

@include xl() {
  .player-desc {
    margin-left: 0;
  }
}

@include not-desktop() {
  .player-window {
    z-index: 14;
  }

  .player-main.chat-open {
    position: fixed;
    top: 0;
    bottom: 0;
    display: grid;
    grid-gap: 0;
    grid-template-rows: min-content 1fr;

    .player-window {
      grid-row: 1 / 2;
      position: unset;
    }

    .chat {
      grid-row: 2 / 2;
      min-height: 100%;
    }

    .player-desc,
    .player-message,
    .mobile-content,
    .mobile-tabs,
    .streams,
    .support,
    .replays-tab,
    .tailgate-tab,
    .highlights,
    .leaderboard,
    .equip {
      display: none;
    }
  }
}

@include not-desktop() {
  .player-desc{
    position: relative;
  }
}

.desktop-stream-list {
  display: flex;

  @include not-desktop() {
    display: none;
  }
}

.mobile-stream-list {
  display: none;

  @include not-desktop() {
    display: flex;
  }
}

.player-main.player-main.chat-open {

  .mobile-stream-list,
  .desktop-stream-list {
    display: none !important;
  }
}
</style>
<style lang="scss">
.event-tabs {
  background-color: $color-gray-x-light;
  border-bottom: $border-width-thin solid $color-gray-light;

  & .tab-header {
    border-bottom: none;
  }
}

.event-tabs,
.mobile-tabs {
  & .tab-header {
    text-wrap: nowrap;
  }
}
</style>